import React, { useState, useEffect } from 'react';
import { Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { CafyLogoPlaceholder, ECareLogo } from '../../ui/Logo';
import { useTaxonomyContext } from '../../../context/TaxonomyContext';
import './GoalSettingFlow.css';

const GoalSettingFlow = ({ onComplete, onCancel }) => {
	const { taxonomyFlow } = useTaxonomyContext();
	const [currentStep, setCurrentStep] = useState('initial');
	const [currentFlow, setCurrentFlow] = useState(null);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [selectedOptions, setSelectedOptions] = useState({});
	const [completedFlows, setCompletedFlows] = useState(new Set());
	const [finalSelections, setFinalSelections] = useState([]);
	const [showingAfterSelection, setShowingAfterSelection] = useState(false);

	const flows = taxonomyFlow?.flows ?? {};
	const initialDialogue = taxonomyFlow?.initial_dialogue ?? { options: [] };

	// Scroll to top function
	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
	};

	// Scroll to top whenever the step changes
	useEffect(() => {
		scrollToTop();
	}, [currentStep, currentStepIndex, showingAfterSelection]);

	// One question is one screen the person answers: the initial dialogue, each
	// flow's sub-domain step, and one term step per sub-domain they picked. The
	// message_after_selection screen replies to the sub-domain step rather than
	// asking anything, so it does not take a number of its own.

	// The flows still to walk, in the order `navigateToNextFlow` walks them —
	// the order of the initial dialogue, not the order the person happened to
	// tick the domains in.
	const selectedFlowIds = () =>
		initialDialogue.options
			.filter(
				(option) => selectedOptions.initial?.[option.id] && option.next_flow
			)
			.map((option) => option.next_flow);

	// The sub-domains of a flow that open a term step. Until its sub-domain step
	// has been answered there is nothing to go on, so every option that could
	// open one stands in and the count only ever shrinks towards the real one.
	// Counting an unanswered flow as zero — what this used to do — let the
	// numerator catch the denominator: arriving at the second flow's sub-domain
	// step with two sub-domains picked in the first, current and total were both
	// 5 and the bar read 100% with a whole flow still ahead.
	const detailStepsFor = (flowId) => {
		const mainStep = flows[flowId]?.steps?.[0];
		if (!mainStep) return [];
		const picked = selectedOptions[`${flowId}_0`] || {};
		const answered = Object.values(picked).some(Boolean);
		return mainStep.options.filter(
			(option) => option.next_step && (!answered || picked[option.id])
		);
	};

	const calculateTotalQuestions = () =>
		selectedFlowIds().reduce(
			(total, flowId) => total + 1 + detailStepsFor(flowId).length,
			1 // the initial dialogue
		);

	const calculateCurrentQuestion = () => {
		if (currentStep === 'initial') return 1;
		if (currentStep === 'complete') return calculateTotalQuestions();

		let question = 1; // the initial dialogue
		for (const flowId of selectedFlowIds()) {
			question += 1; // this flow's sub-domain step
			if (flowId !== currentFlow) {
				question += detailStepsFor(flowId).length;
				continue;
			}
			// Term steps of this flow already behind us. On the sub-domain step
			// itself, and on its message_after_selection, that is none.
			if (currentStepIndex > 0) {
				const stepId = flows[flowId]?.steps?.[currentStepIndex]?.id;
				const reached = detailStepsFor(flowId).findIndex(
					(option) => option.next_step === stepId
				);
				question += reached + 1;
			}
			break;
		}
		return question;
	};

	const handleOptionSelect = (optionId, isMultiple = false) => {
		const stepKey =
			currentStep === 'initial'
				? 'initial'
				: `${currentFlow}_${currentStepIndex}`;


		if (isMultiple) {
			setSelectedOptions((prev) => ({
				...prev,
				[stepKey]: {
					...prev[stepKey],
					[optionId]: !prev[stepKey]?.[optionId],
				},
			}));
		} else {
			setSelectedOptions((prev) => ({
				...prev,
				[stepKey]: { [optionId]: true },
			}));
		}
	};

	const handleNext = () => {

		if (currentStep === 'initial') {
			const selectedFlows = Object.keys(selectedOptions.initial || {}).filter(
				(key) => selectedOptions.initial[key]
			);


			if (selectedFlows.length > 0) {
				// Find the corresponding flow ID from the initial dialogue options
				const firstFlowOption = initialDialogue.options.find((opt) =>
					selectedFlows.includes(opt.id)
				);

				if (firstFlowOption && firstFlowOption.next_flow) {
					const firstFlow = firstFlowOption.next_flow;
					setCurrentFlow(firstFlow);
					setCurrentStepIndex(0);
					setShowingAfterSelection(false);
					setCurrentStep('flow');
				}
			}
		} else if (currentStep === 'flow') {
			const currentFlowData = flows[currentFlow];
			const currentStepData = currentFlowData.steps[currentStepIndex];
			const stepKey = `${currentFlow}_${currentStepIndex}`;

			// If we just showed message_after_selection, handle the next step logic
			if (showingAfterSelection) {
				setShowingAfterSelection(false);

				// Now we need to navigate to the selected subcategory steps
				const selectedOptionIds = Object.keys(
					selectedOptions[stepKey] || {}
				).filter((key) => selectedOptions[stepKey][key]);

				// Find the first selected option that has a next_step
				const selectedOption = currentStepData.options.find(
					(opt) => selectedOptionIds.includes(opt.id) && opt.next_step
				);

				if (selectedOption) {
					// Navigate to the specific step
					const nextStepIndex = currentFlowData.steps.findIndex(
						(step) => step.id === selectedOption.next_step
					);

					if (nextStepIndex !== -1) {
						setCurrentStepIndex(nextStepIndex);
						return;
					}
				}

				// If no specific next step, check if there are more steps in current flow
				if (currentStepIndex < currentFlowData.steps.length - 1) {
					setCurrentStepIndex(currentStepIndex + 1);
				} else {
					// Flow completed, move to next flow or summary
					navigateToNextFlow();
				}
				return;
			}

			// Check if current step is a main step with message_after_selection
			if (currentStepData.message_after_selection && currentStepIndex === 0) {
				// Show the message_after_selection
				setShowingAfterSelection(true);
				return;
			}

			// For detail steps, check if we need to navigate to another selected subcategory
			if (currentStepIndex > 0) {
				// We're in a detail step, check if there are more selected subcategories to visit
				const mainStepKey = `${currentFlow}_0`;
				const selectedMainOptions = Object.keys(
					selectedOptions[mainStepKey] || {}
				).filter((key) => selectedOptions[mainStepKey][key]);

				const mainStepData = currentFlowData.steps[0];
				const selectedSubcategories = mainStepData.options.filter(
					(opt) => selectedMainOptions.includes(opt.id) && opt.next_step
				);

				// Find the current subcategory index
				const currentSubcategoryIndex = selectedSubcategories.findIndex(
					(subcat) => subcat.next_step === currentStepData.id
				);

				// Check if there's a next subcategory to visit
				if (
					currentSubcategoryIndex !== -1 &&
					currentSubcategoryIndex < selectedSubcategories.length - 1
				) {
					const nextSubcategory =
						selectedSubcategories[currentSubcategoryIndex + 1];
					const nextStepIndex = currentFlowData.steps.findIndex(
						(step) => step.id === nextSubcategory.next_step
					);

					if (nextStepIndex !== -1) {
						setCurrentStepIndex(nextStepIndex);
						return;
					}
				}

				// No more subcategories in this flow, move to next flow
				navigateToNextFlow();
				return;
			}

			// Default case: move to next step or next flow
			if (currentStepIndex < currentFlowData.steps.length - 1) {
				setCurrentStepIndex(currentStepIndex + 1);
			} else {
				navigateToNextFlow();
			}
		}
	};

	const navigateToNextFlow = () => {
		// Flow completed, mark as completed
		const newCompletedFlows = new Set([...completedFlows, currentFlow]);
		setCompletedFlows(newCompletedFlows);

		// Find next uncompleted flow
		const initialSelections = Object.keys(selectedOptions.initial || {}).filter(
			(key) => selectedOptions.initial[key]
		);

		const nextFlowOption = initialDialogue.options.find(
			(opt) =>
				initialSelections.includes(opt.id) &&
				!newCompletedFlows.has(opt.next_flow) &&
				opt.next_flow !== currentFlow
		);

		if (nextFlowOption && nextFlowOption.next_flow) {
			setCurrentFlow(nextFlowOption.next_flow);
			setCurrentStepIndex(0);
			setShowingAfterSelection(false);
		} else {
			// All flows completed, collect selections and go directly to complete
			// Use setTimeout to ensure state is updated before collecting
			setTimeout(() => {
				collectFinalSelections();
			}, 0);
			setCurrentStep('complete');
		}
	};

	const collectFinalSelections = () => {

		const selections = [];

		// Go through all selectedOptions
		Object.keys(selectedOptions).forEach((stepKey) => {
			// The initial dialogue picks flows, not goals.
			if (stepKey === 'initial') {
				return;
			}

			const options = selectedOptions[stepKey];
			// Handle flow IDs that contain underscores (like "physical_health")
			const parts = stepKey.split('_');
			const stepIndex = parts.pop(); // Get the last part (step index)
			const flowId = parts.join('_'); // Join the remaining parts back together
			const stepIndexNum = parseInt(stepIndex);


			// Process each selected option
			Object.keys(options).forEach((optionId) => {
				if (options[optionId]) {

					// Get the option data
					const flowData = flows[flowId];
					const stepData = flowData?.steps[stepIndexNum];
					const optionData = stepData?.options.find(
						(opt) => opt.id === optionId
					);

					if (optionData) {

						// Include if it's a detail step (>0) OR has goal_setting: true
						if (stepIndexNum > 0 || optionData.goal_setting === true) {
							const selection = {
								id: optionId,
								name: optionData.name || optionData.text || optionId,
								short_description:
									optionData.short_description ||
									optionData.definition ||
									'No description available',
							};


							// Avoid duplicates
							if (!selections.some((s) => s.id === selection.id)) {
								selections.push(selection);
							}
						} else {
						}
					} else {
					}
				}
			});
		});


		setFinalSelections(selections);
	};

	const handleBack = () => {
		if (currentStep === 'flow') {
			if (showingAfterSelection) {
				setShowingAfterSelection(false);
				return;
			}

			if (currentStepIndex > 0) {
				setCurrentStepIndex(currentStepIndex - 1);
			} else {
				setCurrentStep('initial');
				setCurrentFlow(null);
			}
		}
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const handleComplete = () => {
		if (onComplete) {
			onComplete(true, finalSelections);
		}
	};

	const renderStepWithLogo = (
		title,
		message,
		children,
		showContinueButton = false
	) => (
		<div className='step-container'>
			<div className='step-header'>
				<h2 className='step-title step-title-centered'>{title}</h2>
				<div className='logo-message-container'>
					<CafyLogoPlaceholder size='medium' className='logo-placeholder' />
					<p
						className='step-message step-message-with-logo'
						dangerouslySetInnerHTML={{ __html: message }}
					/>
				</div>
			</div>
			{children}
			{showContinueButton && (
				<div className='navigation'>
					<button onClick={handleBack} className='back-button'>
						<ArrowLeft size={16} />
						<span>Back</span>
					</button>
					<button
						onClick={handleNext}
						className='next-button next-button-enabled'
					>
						<span>Continue</span>
						<ArrowRight size={16} />
					</button>
				</div>
			)}
		</div>
	);

	const renderOptionBox = (option, isSelected, isMultiple, stepKey) => (
		<div
			key={option.id}
			onClick={() => handleOptionSelect(option.id, isMultiple)}
			className={`option-box ${isSelected ? 'option-box-selected' : ''}`}
		>
			<div className='option-content'>
				<div className='option-text-container'>
					<p className='option-text'>{option.text}</p>
				</div>

				<div className='option-controls'>
					<div className={`checkbox ${isSelected ? 'checkbox-selected' : ''}`}>
						{isSelected && <Check size={12} color='white' />}
					</div>
				</div>
			</div>
		</div>
	);

	const getSelectedAreasNames = () => {
		const stepKey = `${currentFlow}_${currentStepIndex}`;
		const selectedIds = Object.keys(selectedOptions[stepKey] || {}).filter(
			(key) => selectedOptions[stepKey][key]
		);

		const flowData = flows[currentFlow];
		const stepData = flowData.steps[currentStepIndex];
		// Name each area by its label alone — "Let's take a closer look at Mood &
		// Emotion." The line the person clicked leads with that same label and
		// then explains it ("Mood & Emotion - feeling anxious, low, angry, or
		// unmotivated."), so carrying the whole line into the sentence repeats
		// what they just read and buries the reply, the more so with several
		// areas picked. `name` is that leading label; splitting `text` at its
		// dash is the fallback for an option that has no name.
		const selectedNames = selectedIds
			.map((id) => {
				const option = stepData.options.find((opt) => opt.id === id);
				const label = option?.name || option?.text?.split(/\s+[-–—]\s+/)[0];
				return label?.trim().replace(/\.+$/, '');
			})
			.filter(Boolean);

		return selectedNames.join(', ');
	};

	const renderCurrentStep = () => {
		if (currentStep === 'initial') {
			const hasSelections = Object.keys(selectedOptions.initial || {}).some(
				(key) => selectedOptions.initial[key]
			);

			return renderStepWithLogo(
				initialDialogue.title,
				initialDialogue.message,
				<>
					<div className='options-container'>
						{initialDialogue.options.map((option) => {
							const isSelected = selectedOptions.initial?.[option.id] || false;
							return renderOptionBox(option, isSelected, true, 'initial');
						})}
					</div>

					<div className='navigation'>
						<button onClick={handleBack} className='back-button'>
							<ArrowLeft size={16} />
							<span>Back</span>
						</button>

						<button
							onClick={handleNext}
							disabled={!hasSelections}
							className={`next-button ${
								hasSelections ? 'next-button-enabled' : 'next-button-disabled'
							}`}
						>
							<span>Continue</span>
							<ArrowRight size={16} />
						</button>
					</div>
				</>
			);
		}

		if (currentStep === 'flow') {
			const flowData = flows[currentFlow];
			if (!flowData) {
				console.error('Flow data not found for:', currentFlow);
				return <div>Error: Flow not found</div>;
			}

			const stepData = flowData.steps[currentStepIndex];
			if (!stepData) {
				console.error('Step data not found for index:', currentStepIndex);
				return <div>Error: Step not found</div>;
			}

			// Show message_after_selection if we're in that state
			if (showingAfterSelection && stepData.message_after_selection) {
				const selectedAreasNames = getSelectedAreasNames();
				const messageWithAreas = stepData.message_after_selection.replace(
					'[selected_areas_name]',
					`<strong>${selectedAreasNames}</strong>`
				);

				return renderStepWithLogo(flowData.title, messageWithAreas, null, true);
			}

			const stepKey = `${currentFlow}_${currentStepIndex}`;
			const isMultiple = stepData.allow_multiple !== false;

			const hasSelections = Object.keys(selectedOptions[stepKey] || {}).some(
				(key) => selectedOptions[stepKey][key]
			);

			return renderStepWithLogo(
				flowData.title,
				stepData.message,
				<>
					<div className='options-container'>
						{stepData.options.map((option) => {
							const isSelected = selectedOptions[stepKey]?.[option.id] || false;
							return renderOptionBox(option, isSelected, isMultiple, stepKey);
						})}
					</div>

					<div className='navigation'>
						<button onClick={handleBack} className='back-button'>
							<ArrowLeft size={16} />
							<span>Back</span>
						</button>

						<button
							onClick={handleNext}
							disabled={!hasSelections}
							className={`next-button ${
								hasSelections ? 'next-button-enabled' : 'next-button-disabled'
							}`}
						>
							<span>Continue</span>
							<ArrowRight size={16} />
						</button>
					</div>
				</>
			);
		}

		if (currentStep === 'complete') {
			return (
				<div className='intro-container'>
					<div className='intro-icon intro-icon-success'>
						<Check size={32} color='white' />
					</div>
					<h1 className='intro-title'>Great job!</h1>
					<p className='intro-description'>
						You've successfully identified your self-care priorities. Your
						personalized goals are now ready to help guide your Parkinson's
						management journey.
					</p>
					<button
						onClick={handleComplete}
						className='next-button next-button-enabled'
					>
						View My Goals
					</button>
				</div>
			);
		}
	};

	const totalQuestions = calculateTotalQuestions();
	const currentQuestion = calculateCurrentQuestion();
	// The bar reports questions *answered*, not the one being looked at, so it
	// reaches 100% on the completion screen and nowhere else. Reporting the
	// question in hand meant the final screen of every run sat at 100% with its
	// answer still outstanding.
	const progress =
		currentStep === 'complete' || totalQuestions === 0
			? 100
			: Math.max(
					0,
					Math.min(
						100,
						Math.round(((currentQuestion - 1) / totalQuestions) * 100)
					)
			  );

	return (
		<div className='goal-flow-container'>
			{/* Header with logo */}
			<div className='goal-flow-header'>
				<div className='header-content'>
					<ECareLogo size='medium' />
					<button onClick={handleCancel} className='cancel-button'>
						Close
					</button>
				</div>
			</div>

			{/* Progress bar */}
			<div className='progress-section'>
				<div className='progress-container'>
					<div className='progress-header'>
						{/* Before a domain is picked there is no run to size yet, so the count
						    is left off rather than claiming "1 of 1". */}
						<span className='progress-text'>
							{currentStep === 'complete'
								? 'Complete!'
								: totalQuestions > 1
								? `Question ${currentQuestion} of ${totalQuestions}`
								: 'Question 1'}
						</span>
						<span className='progress-text'>{progress}%</span>
					</div>
					<div className='progress-bar'>
						<div className='progress-fill' style={{ width: `${progress}%` }} />
					</div>
				</div>
			</div>

			{/* Main content */}
			<div className='main-content'>{renderCurrentStep()}</div>
		</div>
	);
};

export default GoalSettingFlow;
